import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/api';

const API_URL = `${API_BASE_URL}/api`;

const EditSchemeDialog = ({ open, onClose, scheme, onSuccess }) => {
  const [formData, setFormData] = useState({
    monthlyInstallment: '',
    startDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && scheme) {
      setFormData({
        monthlyInstallment: scheme.MonthlyInstallment || '',
        startDate: scheme.StartDate ? new Date(scheme.StartDate).toISOString().split('T')[0] : ''
      });
    }
  }, [open, scheme]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.monthlyInstallment || parseFloat(formData.monthlyInstallment) <= 0) {
      setError('Please enter a valid monthly installment amount');
      return;
    }

    if (!formData.startDate) {
      setError('Please select a start date');
      return;
    }

    // Allow editing but show warning
    // Backend will validate if changes are safe

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/schemes/${scheme.SchemeId}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || 'Scheme updated successfully');
      
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating scheme:', err);
      setError(err.response?.data?.error?.message || 'Failed to update scheme');
      toast.error(err.response?.data?.error?.message || 'Failed to update scheme');
    } finally {
      setLoading(false);
    }
  };

  if (!scheme) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          color: '#fff'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#2a2a2a',
        borderBottom: '2px solid #C59A58',
        color: '#fff'
      }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#C59A58' }}>
            Edit Scheme
          </Typography>
          <Typography variant="caption" sx={{ color: '#ccc' }}>
            Scheme #{scheme.SchemeId} • {scheme.CustomerName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, bgcolor: '#1a1a1a' }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              bgcolor: '#2d1b1b',
              color: '#ffcdd2',
              '& .MuiAlert-icon': { color: '#f44336' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {scheme.PaymentCount > 0 && !isNaN(scheme.PaymentCount) && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              bgcolor: '#2d2416',
              color: '#fff3cd',
              '& .MuiAlert-icon': { color: '#ff9800' }
            }}
          >
            Warning: This scheme has {scheme.PaymentCount} payment(s) recorded. Changes may affect existing payment data.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            label="Monthly Installment"
            type="number"
            value={formData.monthlyInstallment}
            onChange={handleChange('monthlyInstallment')}
            slotProps={{
              input: {
                startAdornment: <Typography sx={{ mr: 1, color: '#C59A58' }}>₹</Typography>
              }
            }}
            helperText="Amount customer pays each month"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#2e2e2e',
                color: '#fff',
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' }
              },
              '& .MuiInputLabel-root': { color: '#ccc' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' },
              '& .MuiFormHelperText-root': { color: '#999' }
            }}
          />

          <TextField
            fullWidth
            required
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={handleChange('startDate')}
            slotProps={{
              inputLabel: {
                shrink: true
              }
            }}
            helperText="Scheme start date (expiry will be calculated as start date + 24 months)"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#2e2e2e',
                color: '#fff',
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' }
              },
              '& .MuiInputLabel-root': { color: '#ccc' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' },
              '& .MuiFormHelperText-root': { color: '#999' }
            }}
          />

          <Alert 
            severity="info"
            sx={{
              bgcolor: '#1a2332',
              color: '#b3d9ff',
              '& .MuiAlert-icon': { color: '#2196f3' }
            }}
          >
            <Typography variant="body2">
              <strong>Note:</strong> Editing the start date will recalculate the expiry date (24 months from start).
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#2a2a2a', borderTop: '1px solid #555' }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: '#ccc', '&:hover': { bgcolor: '#333' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <EditIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{ 
            bgcolor: '#C59A58', 
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#8B7355' },
            '&:disabled': { bgcolor: '#555', color: '#999' }
          }}
        >
          {loading ? 'Updating...' : 'Update Scheme'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSchemeDialog;
